"use client";

import React, { useEffect, useState, useRef } from 'react';
import styles from './ReportsGrid.module.css';
import Link from 'next/link';
import { API_CONFIG } from '@/config/api.config';
import QuickViewModal from './QuickViewModal';

interface Report {
    slug: string;
    title: string;
    summary: string; // Added summary
    primary_industry_name: string;
    price_amount: number;
    content_id: number;
    published_year: number;
}

interface ReportsGridProps {
    industryKey: string;
    initialReports?: Report[];
    subIndustryId?: string | null;
}

const ReportsGrid: React.FC<ReportsGridProps> = ({ industryKey, initialReports, subIndustryId }) => {
    const [reports, setReports] = useState<Report[]>(initialReports || []);
    const [loading, setLoading] = useState(!initialReports);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState<any>(null);
    const [loadingModal, setLoadingModal] = useState(false);
    const shouldFetchRef = useRef(!initialReports);

    useEffect(() => {
        if (initialReports) {
            setReports(initialReports);
            setLoading(false);
            shouldFetchRef.current = false;
        }
    }, [initialReports]);

    useEffect(() => {
        const fetchReports = async () => {
            setLoading(true);
            setError(null);
            try {
                const API_BASE = API_CONFIG.API_GATEWAY;
                const TENANT_ID = API_CONFIG.TENANT_ID;

                let url = `${API_BASE}/v1/public/reports?industry_key=${industryKey}`;
                if (subIndustryId) {
                    url += `&sub_industry_id=${subIndustryId}`;
                }

                const response = await fetch(url, {
                    headers: {
                        "X-Tenant-Id": TENANT_ID,
                        "Content-Type": "application/json"
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch reports');
                }

                const data = await response.json();
                const reportList = Array.isArray(data) ? data : (data.items || data.data || []);
                setReports(reportList);
            } catch (err: any) {
                console.error('Error fetching reports:', err);
                setError(err.message || 'An error occurred while fetching reports');
            } finally {
                setLoading(false);
            }
        };

        if (industryKey) {
            if (!shouldFetchRef.current) {
                shouldFetchRef.current = true;
                return;
            }
            fetchReports();
        }
    }, [industryKey, subIndustryId]);

    const handleQuickView = async (slug: string) => {
        setLoadingModal(true);
        setIsModalOpen(true);

        try {
            const API_BASE = API_CONFIG.API_GATEWAY;
            const TENANT_ID = API_CONFIG.TENANT_ID;

            const response = await fetch(`${API_BASE}/v1/public/reports/${slug}?include_related=true`, {
                headers: {
                    "X-Tenant-Id": TENANT_ID,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch report details');
            }

            const data = await response.json();
            setSelectedReport(data);
        } catch (err: any) {
            console.error('Error fetching report details:', err);
            setIsModalOpen(false);
        } finally {
            setLoadingModal(false);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setTimeout(() => setSelectedReport(null), 400); // Clear after animation
    };

    if (loading) return <div className={styles.loading}>Loading reports...</div>;
    if (error) return <div className={styles.error}>Error: {error}</div>;
    if (reports.length === 0) return <div className={styles.no_data}>No reports found for this industry.</div>;

    return (
        <>
            <div className={styles.reports_grid}>
                {reports.map((report) => (
                    <div key={report.content_id || report.slug} className={styles.report_card}>
                        <div className={styles.report_title}>
                            {report.title}
                            {report.summary ? ` - ${report.summary.split(' ').slice(0, 20).join(' ')}${report.summary.split(' ').length > 30 ? '...' : ''}` : ''}
                        </div>
                        <div className={styles.card_actions}>
                            <button
                                onClick={() => handleQuickView(report.slug)}
                                className={styles.quick_view_btn}
                            >
                                Quick View
                            </button>
                            <Link
                                href={`/report/${report.slug}`}
                                className={styles.view_report_btn}
                            >
                                View Report
                            </Link>
                        </div>
                    </div>
                ))}
            </div>

            <QuickViewModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                reportData={selectedReport}
            />
        </>
    );
};

export default ReportsGrid;

